// Details about a record that aren't its content: who owns it, how often it's checked, and any review request.
// Kept in the change log (rows "meta.<bucket>.<id>"), which only the Smart CMS reads
import { toast } from "sonner"
import { cmsDb } from "./client"
import { META_ID_PREFIX } from "./historyAdapter"
import type { PendingArea } from "./staging"
const TABLE = "scms_change_entry"
const BACKEND: Record<PendingArea, string> = { articles: "articles", services: "service-map", site: "site-config" }

export interface Person {
  id: string
  name: string
}

export interface ReviewRequest {
  // Who should review the unpublished changes, and who asked
  assignee?: Person
  requestedBy?: string
  requestedAt: number
  note?: string
}

export interface RecordMeta {
  owner?: Person
  // How often the record should be checked; 0 or unset uses the section default
  reviewEveryMonths?: number
  // When someone last confirmed the record is still accurate
  verifiedAt?: number
  verifiedBy?: string
  review?: ReviewRequest
}

// bucket:id -> meta, same keys as unpublished changes
export type MetaMap = Record<string, RecordMeta>

const rowId = (key: string) => {
  const i = key.indexOf(":")
  return `${META_ID_PREFIX}${key.slice(0, i)}.${key.slice(i + 1)}`
}

const errText = (e: any) => e?.message || String(e)

export async function loadRecordMeta(team: string, area: PendingArea): Promise<MetaMap> {
  const { data, error } = await cmsDb.from(TABLE)
    .select("entity_kind, entity_id, after")
    .eq("team_id", team).eq("backend", BACKEND[area])
    .like("id", `${META_ID_PREFIX}%`).eq("reverted", false)
  if (error) throw new Error(errText(error))
  const out: MetaMap = {}
  for (const r of data ?? []) {
    if (r.entity_kind && r.entity_id && r.after?.meta) out[`${r.entity_kind}:${r.entity_id}`] = r.after.meta
  }
  return out
}

export async function saveRecordMeta(team: string, area: PendingArea, key: string, meta: RecordMeta | null, author?: { name?: string; email?: string }) {
  const i = key.indexOf(":")
  const empty = !meta || !Object.values(meta).some(v => v !== undefined && v !== null && v !== "")
  const { error } = await cmsDb.from(TABLE).upsert({
    id: rowId(key), team_id: team, backend: BACKEND[area], at_ms: Date.now(),
    action: "update", actor: "user",
    author_name: author?.name ?? null, author_email: author?.email ?? null, author_id: null, via_model: null,
    entity_kind: key.slice(0, i), entity_id: key.slice(i + 1), entity_name: "", locale: null,
    summary: "Record details", before: null,
    after: empty ? null : { meta },
    reverted: empty,
  })
  if (error) toast.error(`Couldn't save the record's details: ${errText(error)}`, { id: "meta-save" })
}
